import React, { useState } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { pdfApi } from '../services/api';
import PDFViewerEnhanced from './PDFViewerEnhanced';
import { useCampus } from '../contexts/CampusContext';

const SearchTab = () => {
  const { campusAffiliation, campusLabel, isAllCampuses, openCampusModal } = useCampus();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentPDF, setCurrentPDF] = useState({ url: '', filename: '', searchText: null, pageNumber: null });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      const data = await pdfApi.searchPDFs(searchQuery.trim(), campusAffiliation);
      setSearchResults(data.results || []);
    } catch (error) {
      // console.error('Search error:', error);
      // Extract error message from response
      let errorMessage = 'Failed to search PDFs. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const highlightSearchTerms = (text, query) => {
    if (!query || !text) return text;
    
    const terms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
    let highlightedText = text;
    
    terms.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    });
    
    return highlightedText;
  };

  const handleViewPDF = (source, content = null) => {
    if (!source) return;
    const pdfUrl = pdfApi.getPDFUrl(source);
    
    // Extract page number and search text from content
    let pageNumber = null;
    let searchText = null;
    
    if (content) {
      // Look for page markers like [Page 5]
      const pageMatch = content.match(/\[Page (\d+)\]/);
      if (pageMatch) {
        pageNumber = parseInt(pageMatch[1], 10);
      }
      
      // Smart text extraction: get the most relevant paragraph near the page marker
      let cleanedText = content.replace(/\[Page \d+\]/g, '').trim();
      
      if (cleanedText.length > 0) {
        // Split into sentences (rough approximation)
        const sentences = cleanedText.split(/(?<=[.!?])\s+/);
        
        // If we have multiple sentences, try to get 2-3 complete sentences (up to ~300 chars)
        let extractedText = '';
        for (const sentence of sentences) {
          if (extractedText.length + sentence.length <= 300) {
            extractedText += (extractedText ? ' ' : '') + sentence;
          } else if (extractedText.length === 0) {
            // First sentence is too long, take first 300 chars
            extractedText = sentence.substring(0, 300);
            break;
          } else {
            break;
          }
        }
        
        searchText = extractedText.trim();
        
        // If searchText is too short, use more context
        if (searchText.length < 30 && cleanedText.length > 0) {
          searchText = cleanedText.substring(0, 350).trim();
        }
        
        // Final check: if still too short, don't use it
        if (searchText.length < 15) {
          searchText = null;
        }
      }
    }
    
    setCurrentPDF({ url: pdfUrl, filename: source, searchText, pageNumber });
    setShowPDFViewer(true);
  };

  const renderSearchResult = (result, index) => {
    const { metadata = {}, content = '', text = '' } = result;
    const source = metadata.source || metadata.filename || result.filename || `Document ${index + 1}`;
    const displayContent = content || text;
    
    // Clean up source name
    let cleanSource = source;
    if (cleanSource.includes('.pdf')) {
      cleanSource = cleanSource.replace('.pdf', '');
    }
    cleanSource = cleanSource.replace(/[-_]+/g, ' ').trim();

    return (
      <Card key={index} className="mb-3 pdf-item fade-in">
        <Card.Body>
          <Card.Title className="pdf-title">
            <i className="fas fa-file-pdf me-2 text-ut-orange"></i>
            {cleanSource}
          </Card.Title>
          
          <Card.Text 
            className="pdf-preview"
            dangerouslySetInnerHTML={{
              __html: highlightSearchTerms(
                displayContent.length > 300 ? displayContent.substring(0, 300) + '...' : displayContent,
                searchQuery
              )
            }}
          />
          
          <div className="pdf-actions">
            <Button
              variant="ut-secondary"
              size="sm"
              onClick={() => handleViewPDF(source, displayContent)}
              title="View PDF with highlighted text"
            >
              <i className="fas fa-eye me-2"></i>View PDF
            </Button>
            
            {metadata.page_number && (
              <span className="text-muted ms-3">
                <i className="fas fa-file-alt me-1"></i>
                Page {metadata.page_number}
              </span>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      <div className="mb-4">
        <h4 className="text-ut-blue mb-3">
          <i className="fas fa-search me-2"></i>
          Search Policy Documents
        </h4>
        <p className="text-muted">
          Search through all uploaded policy documents to find specific information.
        </p>
        <button type="button" className="policy-scope-inline" onClick={openCampusModal}>
          <i className="fas fa-filter"></i>
          <span>Scope: {isAllCampuses ? 'All Campuses' : `system-wide + ${campusLabel || 'selected campus'}`}</span>
        </button>
      </div>

      {/* Search Form */}
      <Form className="mb-4">
        <div className="d-flex gap-2">
          <Form.Control
            type="text"
            placeholder="Enter search terms (e.g., 'academic dishonesty', 'safety requirements')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSearching}
          />
          <Button
            variant="ut-primary"
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isSearching}
          >
            {isSearching ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Searching...
              </>
            ) : (
              <>
                <i className="fas fa-search me-2"></i>
                Search
              </>
            )}
          </Button>
        </div>
      </Form>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Search Results */}
      {hasSearched && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Search Results
              {searchResults.length > 0 && (
                <span className="text-muted ms-2">
                  ({searchResults.length} found)
                </span>
              )}
            </h5>
            {searchQuery && (
              <small className="text-muted">
                Searched for: "<em>{searchQuery}</em>"
              </small>
            )}
          </div>

          {searchResults.length === 0 && !isSearching ? (
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              No documents found matching your search query. Try using different keywords or broader terms.
            </Alert>
          ) : (
            <div className="pdf-list">
              {searchResults.map((result, index) => renderSearchResult(result, index))}
            </div>
          )}
        </div>
      )}

      {/* Search Tips */}
      {!hasSearched && (
        <Card className="mt-4 border-0 bg-light">
          <Card.Body>
            <h6 className="text-ut-blue">
              <i className="fas fa-lightbulb me-2"></i>
              Search Tips
            </h6>
            <ul className="mb-0 text-muted">
              <li>Use specific keywords related to your query</li>
              <li>Try different variations of terms (e.g., "student" vs "undergraduate")</li>
              <li>Use phrases in quotes for exact matches</li>
              <li>Combine multiple keywords for more precise results</li>
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerEnhanced
        show={showPDFViewer}
        onHide={() => setShowPDFViewer(false)}
        pdfUrl={currentPDF.url}
        filename={currentPDF.filename}
        searchText={currentPDF.searchText}
        pageNumber={currentPDF.pageNumber}
      />
    </div>
  );
};

export default SearchTab;
